"use client";

import { Collapsible } from "@universe/ui/components/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@universe/ui/components/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@universe/ui/components/tooltip";
import { cn } from "@universe/ui/lib/utils";
import { ArrowUp, Orbit, Repeat, Settings, Wallet } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Fragment } from "react";

import { Route } from "@/enums/route";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useCustomerAccount } from "@/providers";
import { isRouteActive } from "@/utils/routeSegments";

export type NavMainItem = {
  title: string;
  url: string;
  icon: React.ElementType;
  isActive: boolean;
  route: Route;
  disabled?: boolean;
  tooltip?: string;
};

export function NavMain() {
  const router = useRouter();
  const { isMobile, setOpenMobile } = useSidebar();
  const tNav = useTranslations("nav");
  const pathname = usePathname();
  const { isSettingsEnabled } = useFeatureFlags();
  const { isAccountSuspended } = useCustomerAccount();

  const isActive = (route: Route) => isRouteActive(pathname, route);

  const navItems: NavMainItem[] = [
    {
      title: tNav("dashboard"),
      url: "#",
      icon: Orbit,
      isActive: isActive(Route.Dashboard),
      route: Route.Dashboard,
      disabled: false,
    },
    {
      title: tNav("accounts"),
      url: "#",
      icon: Wallet,
      isActive: isActive(Route.Accounts),
      route: Route.Accounts,
      disabled: false,
    },
    {
      title: tNav("exchange"),
      url: "#",
      icon: Repeat,
      isActive: isActive(Route.Exchange),
      route: Route.Exchange,
      // View-only (suspended) accounts can't trade. "Pay" stays enabled —
      // it is the recipients LIST, which view-only accounts may browse.
      disabled: isAccountSuspended,
      tooltip: isAccountSuspended ? tNav("suspendedDisabled") : undefined,
    },
    {
      title: tNav("pay"),
      url: "#",
      icon: ArrowUp,
      isActive: isActive(Route.Pay),
      route: Route.Pay,
    },
    {
      title: tNav("settings"),
      url: "#",
      icon: Settings,
      isActive: isActive(Route.Settings),
      route: Route.Settings,
      disabled: !isSettingsEnabled,
      tooltip: !isSettingsEnabled ? tNav("comingSoon") : undefined,
    },
  ];

  return (
    <SidebarGroup>
      <SidebarMenu>
        {navItems.map((item) => (
          <Fragment key={item.title}>
            <Collapsible asChild defaultOpen={item.isActive}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={cn(
                        "group/brand-hover relative h-11 hover:bg-transparent active:bg-transparent",
                        item.isActive &&
                          "bg-white/25 scale-101 transition-all duration-200 ease-in-out shadow-lg"
                      )}
                      asChild
                      disabled={item.disabled}
                      tooltip={item.title}
                      onClick={() => {
                        if (item.route && !item.disabled) {
                          router.push(item.route as Route);
                        }
                        if (isMobile) {
                          setOpenMobile(false);
                        }
                      }}
                    >
                      <a
                        href={item.url}
                        data-testid={`nav-${item.route === "/" ? "dashboard" : item.route.slice(1)}`}
                        aria-disabled={item.disabled || undefined}
                        className={cn(
                          "pl-4",
                          item.disabled && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <div className="brand-hover-overlay rounded-md" />
                        <item.icon
                          strokeWidth={item.isActive ? 2 : 1.5}
                          className="!h-5 !w-5 transition-all duration-200 ease-in-out"
                          style={{
                            stroke: "var(--menu-font-color)",
                            filter:
                              "drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)) drop-shadow(0 0px 1px color-mix(in oklch, var(--menu-font-shadow) 60%, transparent))",
                          }}
                        />
                        <span
                          className={cn(
                            "text-lg transition-all duration-200 ease-in-out",
                            item.isActive && "font-semibold text-lg",
                            !item.isActive &&
                              "font-medium tracking-wide opacity-90 hover:opacity-100"
                          )}
                          style={{
                            color: "var(--menu-font-color)",
                            textShadow:
                              "-0.5px 0.5px 1px color-mix(in oklch, var(--menu-font-shadow) 20%, transparent), 0 0.5px 2px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent), 1px 1px 0px color-mix(in oklch, var(--menu-font-shadow) 40%, transparent)",
                          }}
                        >
                          {item.title}
                        </span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </TooltipTrigger>
                {item.tooltip && (
                  <TooltipContent
                    side="right"
                    align="center"
                    className="text-white rounded-lg shadow-lg text-sm font-semibold bg-primary"
                  >
                    <p>{item.tooltip}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </Collapsible>
          </Fragment>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
